import { Form, Input, InputNumber, Select } from 'antd';
import React, { FC } from 'react';
import { IRule } from '../type';
import {
  COLUMN,
  CUSTOM,
  RIGHT,
  DOWN,
  LEFT,
  NEGATIVE,
  POSITIVE,
  UP,
} from '../const';
import { FormType } from '../new';
import ColPosition from '../col-position';
import { isArray } from 'radash';
import PositiveNegativeInput from '../positive-negative-input';

const { Item: FormItem } = Form;
const { Item: StyleItem } = Form;

const ColumnItem: FC<{
  prefix: (string | number)[];
  fullPrefix: (string | number)[];
}> = ({ prefix, fullPrefix }) => {
  const form = Form.useFormInstance();
  return (
    <>
      <ColPosition prefix={prefix} fullPrefix={fullPrefix} />

      <FormItem className="mb-0" label="行范围" shouldUpdate={() => true}>
        <FormItem name={[...prefix, 'row_range_config', 'range_type']}>
          <Select
            options={[
              {
                label: '全部行',
                value: 'ALL',
              },
              {
                label: '自定义',
                value: 'CUSTOM',
              },
            ]}
          />
        </FormItem>
        {/* 当选择 自定义 时，展示 开始列、结束列 */}
        <FormItem shouldUpdate={() => true}>
          {({ getFieldValue }) => {
            const range_type = getFieldValue([
              ...fullPrefix,
              'row_range_config',
              'range_type',
            ]) as IRule['col_content_config']['row_range_config']['range_type'];
            if (range_type === 'ALL') return null;

            return (
              <>
                <RowBeginItem
                  prefix={[...prefix, 'row_range_config', 'begin']}
                  fullPrefix={[...fullPrefix, 'row_range_config', 'begin']}
                />
                <RowEndItem
                  prefix={[...prefix, 'row_range_config', 'end']}
                  fullPrefix={[...fullPrefix, 'row_range_config', 'end']}
                />
              </>
            );
          }}
        </FormItem>
      </FormItem>
    </>
  );
};

/**
 * 行起始和行结束 结构一样
 * RowBeginItem  ColEndItem
 */
const RowBeginItem: FC<{
  prefix: (string | number)[];
  fullPrefix: (string | number)[];
}> = ({ prefix, fullPrefix }) => {
  const form = Form.useFormInstance();
  return (
    <>
      {/* 行起始 和 行结束结构一样 */}
      {/* 行起始 */}
      <>
        <FormItem
          className="mb-0"
          label="行起始"
          name={[...prefix, 'position_method']}
        >
          <Select
            onChange={(e, option) => {
              if (isArray(option)) {
                form.setFieldValue([...fullPrefix, 'offset_config'], undefined);
                return;
              }

              // TODO 行顺序、列顺序的默认值
              const value =
                option.value as IRule['col_content_config']['row_range_config']['begin']['position_method'];

              if (value === 'ORDER') {
                form.setFieldValue([...fullPrefix, 'offset_config'], {
                  direction: POSITIVE,
                  offset: 1,
                });
              } else if (value === 'KEYWORD') {
                form.setFieldValue([...fullPrefix, 'offset_config'], {
                  direction: UP,
                  offset: 0,
                });
              }
            }}
            options={[
              {
                label: `关键词所在行`,
                value: 'KEYWORD',
              },
              {
                label: `行顺序`,
                value: 'ORDER',
              },
            ]}
          />
        </FormItem>

        {/* 列定位 行顺序 展示 正倒数 */}
        <FormItem noStyle shouldUpdate={() => true}>
          {({ getFieldValue }) => {
            const position_method = getFieldValue([
              ...fullPrefix,
              'position_method',
            ]) as IRule['row_content_config']['colum_range_config']['begin']['position_method'];
            // 行顺序
            if (position_method === 'ORDER') {
              return (
                <StyleItem label=" ">
                  <section className="flex gap-4">
                    <FormItem
                      className="flex-1"
                      name={[...prefix, 'offset_config', 'direction']}
                    >
                      <Select
                        options={[
                          {
                            label: '正数',
                            value: POSITIVE,
                          },
                          {
                            label: '倒数',
                            value: NEGATIVE,
                          },
                        ]}
                      />
                    </FormItem>
                    <FormItem
                      className="flex-2"
                      name={[...prefix, 'offset_config', 'offset']}
                      rules={[
                        {
                          required: true,
                          message: '',
                          warningOnly: true,
                          type: 'number',
                        },
                      ]}
                    >
                      <InputNumber className="w-full" min={0} max={99999} />
                    </FormItem>
                  </section>
                </StyleItem>
              );

              // 关键词所在行
            } else if (position_method === 'KEYWORD') {
              return (
                <>
                  <FormItem
                    label=" "
                    name={[...prefix, 'keyword']}
                    rules={[{ required: true, message: '', warningOnly: true }]}
                  >
                    <Input allowClear placeholder="请输入关键词或正则表达式" />
                  </FormItem>
                  <section className="flex gap-4">
                    <FormItem
                      className="flex-1"
                      name={[...prefix, 'offset_config', 'direction']}
                    >
                      <Select
                        options={[
                          {
                            label: '向上偏移',
                            value: UP,
                          },
                          {
                            label: '向下偏移',
                            value: DOWN,
                          },
                        ]}
                      />
                    </FormItem>
                    <FormItem
                      className="flex-2"
                      name={[...prefix, 'offset_config', 'offset']}
                      rules={[
                        {
                          required: true,
                          warningOnly: true,
                          type: 'number',
                          message: '',
                        },
                      ]}
                    >
                      <InputNumber className="w-full" min={0} max={99999} />
                    </FormItem>
                  </section>
                </>
              );
            }
          }}
        </FormItem>
      </>
    </>
  );
};

/**
 * 行起始和行结束 结构一样
 * ColBeginItem  ColEndItem
 */
const RowEndItem: FC<{
  prefix: (string | number)[];
  fullPrefix: (string | number)[];
}> = ({ prefix, fullPrefix }) => {
  const form = Form.useFormInstance();
  return (
    <>
      {/* 行结束 */}
      <FormItem label="行结束" name={[...prefix, 'position_method']}>
        <Select
          onChange={(e, option) => {
            if (isArray(option)) {
              form.setFieldValue([...fullPrefix, 'offset_config'], undefined);
              return;
            }

            // TODO 行顺序、列顺序的默认值
            const value =
              option.value as IRule['col_content_config']['row_range_config']['end']['position_method'];

            if (value === 'ORDER') {
              form.setFieldValue([...fullPrefix, 'offset_config'], {
                direction: NEGATIVE,
                offset: 1,
              });
            } else if (value === 'KEYWORD') {
              form.setFieldValue([...fullPrefix, 'offset_config'], {
                direction: UP,
                offset: 0,
              });
            }
          }}
          options={[
            {
              label: `关键词所在行`,
              value: 'KEYWORD',
            },
            {
              label: `行顺序`,
              value: 'ORDER',
            },
          ]}
        />
      </FormItem>

      {/* 列定位 行顺序 展示 正倒数 */}
      <FormItem noStyle shouldUpdate={() => true}>
        {({ getFieldValue }) => {
          const position_method = getFieldValue([
            ...fullPrefix,
            'position_method',
          ]) as IRule['row_content_config']['colum_range_config']['end']['position_method'];
          // 行顺序
          if (position_method === 'ORDER') {
            return (
              <>
                <FormItem name={[...prefix, 'offset_config']}>
                  <PositiveNegativeInput inputNumberProps={{ suffix: '行' }} />
                </FormItem>
                {/* <StyleItem label=" ">
                  <section className="flex gap-4">
                    <FormItem
                      className="flex-1"
                      name={[...prefix, 'offset_config', 'direction']}
                    >
                      <Select
                        options={[
                          {
                            label: '正数',
                            value: POSITIVE,
                          },
                          {
                            label: '倒数',
                            value: NEGATIVE,
                          },
                        ]}
                      />
                    </FormItem>
                    <FormItem
                      className="flex-2"
                      name={[...prefix, 'offset_config', 'offset']}
                    >
                      <InputNumber
                        className="w-full"
                        max={99999}
                        min={-99999}
                      />
                    </FormItem>
                  </section>
                </StyleItem> */}
              </>
            );

            // 关键词所在行
          } else if (position_method === 'KEYWORD') {
            return (
              <>
                <FormItem
                  name={[...prefix, 'keyword']}
                  rules={[{ required: true, message: '', warningOnly: true }]}
                >
                  <Input allowClear placeholder="请输入关键词或正则表达式" />
                </FormItem>
                <section className="flex gap-4">
                  <FormItem
                    className="flex-1"
                    name={[...prefix, 'offset_config', 'direction']}
                  >
                    <Select
                      options={[
                        {
                          label: '向上偏移',
                          value: UP,
                        },
                        {
                          label: '向下偏移',
                          value: DOWN,
                        },
                      ]}
                    />
                  </FormItem>
                  <FormItem
                    className="flex-2"
                    name={[...prefix, 'offset_config', 'offset']}
                    rules={[
                      {
                        required: true,
                        warningOnly: true,
                        type: 'number',
                        message: '',
                      },
                    ]}
                  >
                    <InputNumber
                      suffix="行"
                      className="w-full"
                      min={0}
                      max={99999}
                    />
                  </FormItem>
                </section>
              </>
            );
          }
        }}
      </FormItem>
    </>
  );
};

export default ColumnItem;
